import React, { useState, useEffect } from 'react'
import { HiPlus } from 'react-icons/hi2'
import { modelsService } from '../services/modelsService'
import { serviceProviderService } from '../services/serviceProviderService'
import { PageHeader } from '../components/ui/PageHeader'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { EmptyState } from '../components/ui/EmptyState'
import { Modal } from '../components/ui/Modal'

const Models = () => {
  const [models, setModels] = useState([])
  const [loading, setLoading] = useState(true)
  const [providers, setProviders] = useState([])
  const [loadingProviders, setLoadingProviders] = useState(false)
  const [selectedModel, setSelectedModel] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [createForm, setCreateForm] = useState({
    model_name: '',
    service_provider: 'gemini',
    model_type: 'LLM',
    input_token_amount: '',
    input_token_counts: '',
    output_token_amount: '',
    output_token_counts: ''
  })
  const [editForm, setEditForm] = useState({
    model_name: '',
    service_provider: 'gemini',
    model_type: 'LLM',
    input_token_amount: '',
    input_token_counts: '',
    output_token_amount: '',
    output_token_counts: ''
  })
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  })

  useEffect(() => {
    // Fetch models from API
    const fetchModels = async () => {
      setLoading(true)
      try {
        const response = await modelsService.getModels({
          page: pagination.currentPage,
          limit: pagination.limit
        })
        
        if (response.success) {
          setModels(response.data.models || [])
          setPagination(response.data.pagination || pagination)
        }
      } catch (error) {
        console.error('Failed to fetch models:', error)
        setModels([])
      } finally {
        setLoading(false)
      }
    }

    fetchModels()
  }, [pagination.currentPage, pagination.limit])

  useEffect(() => {
    const fetchProviders = async () => {
      setLoadingProviders(true)
      try {
        const response = await serviceProviderService.getProviders()
        if (response.success) setProviders(response.data.providers || [])
        else setProviders([])
      } catch (e) {
        setProviders([])
      } finally {
        setLoadingProviders(false)
      }
    }

    fetchProviders()
  }, [])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatCredits = (credits) => {
    if (!credits || isNaN(credits)) return 'N/A'
    return `${Number(credits).toFixed(6)} credits/token`
  }

  const handleCreateModel = async (e) => {
    e.preventDefault()
    setCreateLoading(true)
    
    try {
      const modelData = {
        model_name: createForm.model_name,
        service_provider: createForm.service_provider,
        model_type: createForm.model_type,
        input_token_amount: parseFloat(createForm.input_token_amount),
        input_token_counts: parseFloat(createForm.input_token_counts),
        output_token_amount: parseFloat(createForm.output_token_amount),
        output_token_counts: parseFloat(createForm.output_token_counts)
      }
      
      await modelsService.createModel(modelData)
      
      // Reset form and close modal
      setCreateForm({
        model_name: '',
        service_provider: 'gemini',
        model_type: 'LLM',
        input_token_amount: '',
        input_token_counts: '',
        output_token_amount: '',
        output_token_counts: ''
      })
      setShowCreateModal(false)
      
      // Refresh models list
      const response = await modelsService.getModels({
        page: pagination.currentPage,
        limit: pagination.limit
      })
      
      if (response.success) {
        setModels(response.data.models || [])
        setPagination(response.data.pagination || pagination)
      }
    } catch (error) {
      console.error('Failed to create model:', error)
    } finally {
      setCreateLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setCreateForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleEditInputChange = (e) => {
    const { name, value } = e.target
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleEditModel = (model) => {
    // Calculate the original token amounts from the stored values
    const input_token_amount = model.input_tokens_per_dollar ? (1 / model.input_tokens_per_dollar) : 0
    const output_token_amount = model.output_tokens_per_dollar ? (1 / model.output_tokens_per_dollar) : 0
    const input_token_counts = model.input_tokens_per_dollar ? (1 / model.input_credits_per_token) : 0
    const output_token_counts = model.output_tokens_per_dollar ? (1 / model.output_credits_per_token) : 0

    setEditForm({
      model_name: model.model_name || '',
      service_provider: model.service_provider || 'gemini',
      model_type: model.model_type || 'LLM',
      input_token_amount: input_token_amount.toString(),
      input_token_counts: input_token_counts.toString(),
      output_token_amount: output_token_amount.toString(),
      output_token_counts: output_token_counts.toString()
    })
    setSelectedModel(model)
    setShowEditModal(true)
  }

  const handleUpdateModel = async (e) => {
    e.preventDefault()
    setEditLoading(true)
    
    try {
      const modelData = {
        model_name: editForm.model_name,
        service_provider: editForm.service_provider,
        model_type: editForm.model_type,
        input_token_amount: parseFloat(editForm.input_token_amount),
        input_token_counts: parseFloat(editForm.input_token_counts),
        output_token_amount: parseFloat(editForm.output_token_amount),
        output_token_counts: parseFloat(editForm.output_token_counts)
      }
      
      await modelsService.updateModel(selectedModel.model_id, modelData)
      
      // Close modal
      setShowEditModal(false)
      setSelectedModel(null)
      
      // Refresh models list
      const response = await modelsService.getModels({
        page: pagination.currentPage,
        limit: pagination.limit
      })
      
      if (response.success) {
        setModels(response.data.models || [])
        setPagination(response.data.pagination || pagination)
      }
    } catch (error) {
      console.error('Failed to update model:', error)
    } finally {
      setEditLoading(false)
    }
  }

  return (
    <div>
      <PageHeader
        title="AI models"
        subtitle="Manage model catalog, providers, and token pricing."
        actions={
          <Button type="button" onClick={() => setShowCreateModal(true)}>
            <HiPlus className="mr-2 h-4 w-4" />
            Add model
          </Button>
        }
      />

      <div className="space-y-6">
        {loading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="mb-4 h-4 w-3/4 rounded bg-secondary-200 dark:bg-secondary-700" />
                <div className="mb-2 h-3 w-1/2 rounded bg-secondary-200 dark:bg-secondary-700" />
                <div className="h-3 w-2/3 rounded bg-secondary-200 dark:bg-secondary-700" />
              </div>
            ))}
          </div>
        ) : models.length === 0 ? (
          <EmptyState
            title="No models yet"
            description="Create your first model to define pricing and provider settings."
            action={
              <Button type="button" onClick={() => setShowCreateModal(true)}>
                Add model
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {models.map((model) => (
              <div
                key={model.model_id}
                className="card flex h-full cursor-pointer flex-col transition-shadow hover:shadow-md"
                onClick={() => setSelectedModel(model)}
              >
                <div className="mb-4 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate text-lg font-semibold text-secondary-900 dark:text-secondary-50">{model.model_name}</h3>
                    <p className="text-sm capitalize text-text-muted dark:text-text-muted-dark">{model.service_provider}</p>
                  </div>
                  <Badge variant="primary">{model.model_type}</Badge>
                </div>

                <div className="mb-4 space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="text-text-muted">Input credits</span>
                    <span className="font-medium">{formatCredits(model.input_credits_per_token)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-text-muted">Output credits</span>
                    <span className="font-medium">{formatCredits(model.output_credits_per_token)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-text-muted">Created</span>
                    <span>{formatDate(model.created_at)}</span>
                  </div>
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-2">
                  <Badge>ID {model.model_id}</Badge>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditModel(model)
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && models.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-text-muted dark:text-text-muted-dark">
              Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of {pagination.totalCount}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!pagination.hasPrevPage}
                onClick={() => setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              >
                Previous
              </Button>
              <span className="text-sm text-secondary-700 dark:text-secondary-300">
                {pagination.currentPage} / {pagination.totalPages}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!pagination.hasNextPage}
                onClick={() => setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        <Modal
          open={Boolean(selectedModel) && !showEditModal && !showCreateModal}
          onClose={() => setSelectedModel(null)}
          title={selectedModel?.model_name}
          description={selectedModel ? `${selectedModel.model_type} · ${selectedModel.service_provider}` : undefined}
          size="lg"
        >
          {selectedModel && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-secondary-50 p-3 dark:bg-secondary-800/50">
                  <p className="text-text-muted">Model ID</p>
                  <p className="font-mono font-medium">{selectedModel.model_id}</p>
                </div>
                <div className="rounded-lg bg-secondary-50 p-3 dark:bg-secondary-800/50">
                  <p className="text-text-muted">Created</p>
                  <p>{formatDate(selectedModel.created_at)}</p>
                </div>
                <div className="rounded-lg bg-secondary-50 p-3 dark:bg-secondary-800/50">
                  <p className="text-text-muted">Updated</p>
                  <p>{formatDate(selectedModel.updated_at)}</p>
                </div>
                <div className="rounded-lg bg-secondary-50 p-3 dark:bg-secondary-800/50">
                  <p className="text-text-muted">Type</p>
                  <p>{selectedModel.model_type}</p>
                </div>
              </div>
              <div>
                <h4 className="mb-2 font-semibold">Pricing</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-muted p-3 dark:border-muted-dark">
                    <p className="font-medium text-primary-700 dark:text-primary-300">Input</p>
                    <p className="text-text-muted">Per dollar: {selectedModel.input_tokens_per_dollar ? Number(selectedModel.input_tokens_per_dollar).toFixed(2) : 'N/A'}</p>
                    <p>{formatCredits(selectedModel.input_credits_per_token)}</p>
                  </div>
                  <div className="rounded-lg border border-muted p-3 dark:border-muted-dark">
                    <p className="font-medium text-emerald-700 dark:text-emerald-300">Output</p>
                    <p className="text-text-muted">Per dollar: {selectedModel.output_tokens_per_dollar ? Number(selectedModel.output_tokens_per_dollar).toFixed(2) : 'N/A'}</p>
                    <p>{formatCredits(selectedModel.output_credits_per_token)}</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t border-muted pt-4 dark:border-muted-dark">
                <Button type="button" variant="secondary" onClick={() => setSelectedModel(null)}>Close</Button>
                <Button type="button" onClick={() => handleEditModel(selectedModel)}>Edit</Button>
              </div>
            </div>
          )}
        </Modal>

        {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => setShowCreateModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleCreateModel}>
                <div className="bg-white px-8 pt-8 pb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Create New Model</h3>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Model Name */}
                    <div>
                      <label htmlFor="model_name" className="block text-sm font-medium text-gray-700 mb-2">
                        Model Name *
                      </label>
                      <input
                        type="text"
                        id="model_name"
                        name="model_name"
                        value={createForm.model_name}
                        onChange={handleInputChange}
                        required
                        maxLength={50}
                        className="input-field"
                        placeholder="Enter model name"
                      />
                    </div>

                    {/* Model Type */}
                    <div>
                      <label htmlFor="model_type" className="block text-sm font-medium text-gray-700 mb-2">
                        Model Type *
                      </label>
                      <select
                        id="model_type"
                        name="model_type"
                        value={createForm.model_type}
                        onChange={handleInputChange}
                        required
                        className="input-field"
                      >
                        <option value="LLM">LLM</option>
                        <option value="EMBEDDING">EMBEDDING</option>
                        <option value="VISION">VISION</option>
                        <option value="TEXT_TO_SPEECH">TEXT_TO_SPEECH</option>
                        <option value="SPEECH_TO_TEXT">SPEECH_TO_TEXT</option>
                        <option value="IMAGE_GENERATION">IMAGE_GENERATION</option>
                      </select>
                    </div>

                    {/* Service Provider */}
                    <div>
                      <label htmlFor="service_provider" className="block text-sm font-medium text-gray-700 mb-2">
                        Service Provider *
                      </label>
                      <select
                        id="service_provider"
                        name="service_provider"
                        value={createForm.service_provider}
                        onChange={handleInputChange}
                        required
                        className="input-field"
                      >
                        {loadingProviders ? (
                          <option value="">Loading...</option>
                        ) : providers.length === 0 ? (
                          <>
                            <option value="gemini">gemini</option>
                            <option value="perplexity">perplexity</option>
                            <option value="mistral">mistral</option>
                          </>
                        ) : (
                          providers.map((p) => (
                            <option key={p.provider_code} value={p.provider_code}>
                              {p.provider_name} ({p.provider_code})
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    {/* Input Token Amount */}
                    <div>
                      <label htmlFor="input_token_amount" className="block text-sm font-medium text-gray-700 mb-2">
                        Input Token Amount ($) *
                      </label>
                      <input
                        type="number"
                        id="input_token_amount"
                        name="input_token_amount"
                        value={createForm.input_token_amount}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step="0.01"
                        className="input-field"
                        placeholder="e.g., 1.00"
                      />
                    </div>

                    {/* Input Token Counts */}
                    <div>
                      <label htmlFor="input_token_counts" className="block text-sm font-medium text-gray-700 mb-2">
                        Input Token Counts *
                      </label>
                      <input
                        type="number"
                        id="input_token_counts"
                        name="input_token_counts"
                        value={createForm.input_token_counts}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step="1"
                        className="input-field"
                        placeholder="e.g., 1000000"
                      />
                    </div>

                    {/* Output Token Amount */}
                    <div>
                      <label htmlFor="output_token_amount" className="block text-sm font-medium text-gray-700 mb-2">
                        Output Token Amount ($) *
                      </label>
                      <input
                        type="number"
                        id="output_token_amount"
                        name="output_token_amount"
                        value={createForm.output_token_amount}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step="0.01"
                        className="input-field"
                        placeholder="e.g., 1.00"
                      />
                    </div>

                    {/* Output Token Counts */}
                    <div>
                      <label htmlFor="output_token_counts" className="block text-sm font-medium text-gray-700 mb-2">
                        Output Token Counts *
                      </label>
                      <input
                        type="number"
                        id="output_token_counts"
                        name="output_token_counts"
                        value={createForm.output_token_counts}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step="1"
                        className="input-field"
                        placeholder="e.g., 1000000"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createLoading ? 'Creating...' : 'Create Model'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Model Modal */}
      {showEditModal && selectedModel && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => setShowEditModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleUpdateModel}>
                <div className="bg-white px-8 pt-8 pb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Edit Model</h3>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Model Name */}
                    <div>
                      <label htmlFor="edit_model_name" className="block text-sm font-medium text-gray-700 mb-2">
                        Model Name *
                      </label>
                      <input
                        type="text"
                        id="edit_model_name"
                        name="model_name"
                        value={editForm.model_name}
                        onChange={handleEditInputChange}
                        required
                        maxLength={50}
                        className="input-field"
                        placeholder="Enter model name"
                      />
                    </div>

                    {/* Model Type */}
                    <div>
                      <label htmlFor="edit_model_type" className="block text-sm font-medium text-gray-700 mb-2">
                        Model Type *
                      </label>
                      <select
                        id="edit_model_type"
                        name="model_type"
                        value={editForm.model_type}
                        onChange={handleEditInputChange}
                        required
                        className="input-field"
                      >
                        <option value="LLM">LLM</option>
                        <option value="EMBEDDING">EMBEDDING</option>
                        <option value="VISION">VISION</option>
                        <option value="TEXT_TO_SPEECH">TEXT_TO_SPEECH</option>
                        <option value="SPEECH_TO_TEXT">SPEECH_TO_TEXT</option>
                        <option value="IMAGE_GENERATION">IMAGE_GENERATION</option>
                      </select>
                    </div>

                    {/* Service Provider */}
                    <div>
                      <label htmlFor="edit_service_provider" className="block text-sm font-medium text-gray-700 mb-2">
                        Service Provider *
                      </label>
                      <select
                        id="edit_service_provider"
                        name="service_provider"
                        value={editForm.service_provider}
                        onChange={handleEditInputChange}
                        required
                        className="input-field"
                      >
                        {loadingProviders ? (
                          <option value="">Loading...</option>
                        ) : providers.length === 0 ? (
                          <>
                            <option value="gemini">gemini</option>
                            <option value="perplexity">perplexity</option>
                            <option value="mistral">mistral</option>
                          </>
                        ) : (
                          providers.map((p) => (
                            <option key={p.provider_code} value={p.provider_code}>
                              {p.provider_name} ({p.provider_code})
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    {/* Input Token Amount */}
                    <div>
                      <label htmlFor="edit_input_token_amount" className="block text-sm font-medium text-gray-700 mb-2">
                        Input Token Amount ($) *
                      </label>
                      <input
                        type="number"
                        id="edit_input_token_amount"
                        name="input_token_amount"
                        value={editForm.input_token_amount}
                        onChange={handleEditInputChange}
                        required
                        min="0"
                        step="0.01"
                        className="input-field"
                        placeholder="e.g., 1.00"
                      />
                    </div>

                    {/* Input Token Counts */}
                    <div>
                      <label htmlFor="edit_input_token_counts" className="block text-sm font-medium text-gray-700 mb-2">
                        Input Token Counts *
                      </label>
                      <input
                        type="number"
                        id="edit_input_token_counts"
                        name="input_token_counts"
                        value={editForm.input_token_counts}
                        onChange={handleEditInputChange}
                        required
                        min="0"
                        step="1"
                        className="input-field"
                        placeholder="e.g., 1000000"
                      />
                    </div>

                    {/* Output Token Amount */}
                    <div>
                      <label htmlFor="edit_output_token_amount" className="block text-sm font-medium text-gray-700 mb-2">
                        Output Token Amount ($) *
                      </label>
                      <input
                        type="number"
                        id="edit_output_token_amount"
                        name="output_token_amount"
                        value={editForm.output_token_amount}
                        onChange={handleEditInputChange}
                        required
                        min="0"
                        step="0.01"
                        className="input-field"
                        placeholder="e.g., 1.00"
                      />
                    </div>

                    {/* Output Token Counts */}
                    <div>
                      <label htmlFor="edit_output_token_counts" className="block text-sm font-medium text-gray-700 mb-2">
                        Output Token Counts *
                      </label>
                      <input
                        type="number"
                        id="edit_output_token_counts"
                        name="output_token_counts"
                        value={editForm.output_token_counts}
                        onChange={handleEditInputChange}
                        required
                        min="0"
                        step="1"
                        className="input-field"
                        placeholder="e.g., 1000000"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editLoading ? 'Updating...' : 'Update Model'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default Models
